/**
 * ComponentInitializationUtil.js
 *
 * This utility module handles the initialization of workflow components in the canvas.
 * It provides functionality to:
 * - Determine the appropriate store type for a given component
 * - Initialize component data with proper structure
 * - Save component data to the backend
 *
 * The module is used when new components are added to the workflow canvas,
 * ensuring proper initialization and persistence of component data.
 */

import { send_component_save_request } from './Request';
import { storeMap } from './prefillComponentStores';
import { protectLoadedStore } from './storeWriteGuard';

import useAIAssistantTemplateStore from '../components/AIAssistant/useAIAssistantTemplateStore';
import useAIAgentStore from '../components/AIAgent/useAIAgentStore';
import useTriggerFromAWorkflowStore from '../components/TriggerFromAWorkflow/useTriggerFromAWorkflowStore';
import useTriggerAnotherWorkflowStore from '../components/TriggerAnotherWorkflow/useTriggerAnotherWorkflowStore';
import useEmailTriggerStore from '../components/EmailTrigger/useEmailTriggerStore';
import useWebhookImportStore from '../components/WebhookImport/useWebhookImportStore';
import useWebhookTriggerStore from '../components/WebhookTrigger/useWebhookTriggerStore';
import useBranchPathsStore from '../components/BranchPaths/useBranchPathsStore';
import useMergeWorkflowStore from '../components/MergeWorkflow/useMergeWorkflowStore';
import useTimeDelayStore from '../components/TimeDelay/useTimeDelayStore';
import useQueryDatabaseStore from '../components/QueryDatabase/useQueryDatabaseStore';
import useCreateDatabaseRecordStore from '../components/CreateDatabaseRecord/useCreateDatabaseRecordStore';
import useUpdateDatabaseRecordStore from '../components/UpdateDatabaseRecord/useUpdateDatabaseRecordStore';
import useApiExportStore from '../components/ApiExport/useApiExportStore';
import useCodeStore from '../components/Code/useCodeStore';
import useConditionalWorkflowStore from '../components/ConditionalWorkflow/useConditionalWorkflowStore';
import useDocumentStore from '../components/DocumentEditor/useDocumentStore';
import useCreateFileStore from '../components/CreateFile/useCreateFileStore';
import useScheduleStartStore from '../components/ScheduleStart/useScheduleStartStore';
import useSessionVariableStore from '../components/SessionVariable/useSessionVariableStore';
import FormBuilderContainer from '../components/FormBuilder/FormBuilderContainer';
import SequentialFormContainer from '../components/SequentialForm/SequentialFormContainer';
import useBulkAssessmentStore from '../components/BulkAssessment/useBulkAssessmentStore';
import useAPIResReqStore from '../components/APIRequestAndResponse/useAPIResReqStore';
import useCalculatorStore from '../components/Calculator/useCalculatorStore';
import useEmailStore from '../components/Email/useEmailStore';
import useTransformStore from '../components/ListTransform/useTransformStore';
import useLoopThroughListStore from '../components/LoopThroughList/useLoopThroughListStore';
import useParseJSONStore from '../components/ParseJSON/useParseJSONStore';
import useSlackStore from '../components/Slack/useSlackStore';
import useConditionalLogicStore from '../components/ConditionalLogic/useConditionalLogicStore';
import useConditionalWorkflowPathStore from '../components/ConditionalWorkflowPath/useConditionalWorkflowPathStore';
import useTriggerFromExternalStore from '../components/TriggerFromExternal/useTriggerFromExternalStore';
import usePerformActionExternalStore from '../components/PerformActionExternal/usePerformActionExternalStore';
import useTriggerOnDatabaseUpdateStore from '../components/TriggerOnDatabaseUpdate/useTriggerOnDatabaseUpdateStore';
import useTriggerFromApiPollingStore from '../components/TriggerFromApiPolling/useTriggerFromApiPollingStore';
import useAwaitExternalEventStore from '../components/AwaitExternalEvent/useAwaitExternalEventStore';
import useConvergeThreadsStore from '../components/ConvergeThreads/useConvergeThreadsStore';
import useGatewayStore from '../components/Gateway/useGatewayStore';
import useHttpRequestStore from '../components/HttpRequest/useHttpRequestStore';
import useDynamicFormStore from '../components/DynamicFormBuilder/useDynamicFormStore';
import useDynamicReviewTaskStore from '../components/DynamicReviewTask/useDynamicReviewTaskStore';
import useFormStepStore from '../components/FormStep/useFormStepStore';
import useArtifactStore from '../components/Artifacts/useArtifactStore';
import { defaultStore as useQueryTableV2Store } from '../components/QueryTableV2/useQueryTableV2Store';
import { defaultStore as useCreateRowV2Store } from '../components/CreateRowV2/useCreateRowV2Store';
import { defaultStore as useUpdateRowV2Store } from '../components/UpdateRowV2/useUpdateRowV2Store';
import { defaultStore as useTriggerTableV2Store } from '../components/TriggerTableV2/useTriggerTableV2Store';
import { defaultStore as useTriggerOnArtifactStore } from '../components/TriggerOnArtifact/useTriggerOnArtifactStore';
import useCreateBoardTicketStore from '../components/CreateBoardTicket/useCreateBoardTicketStore';
import useGetBoardTicketStore from '../components/GetBoardTicket/useGetBoardTicketStore';
import useUpdateBoardTicketStore from '../components/UpdateBoardTicket/useUpdateBoardTicketStore';
import useTriggerOnBoardTicketStore from '../components/TriggerOnBoardTicket/useTriggerOnBoardTicketStore';
import useCustomFunctionInstanceStore from '../components/CustomFunctions/useCustomFunctionInstanceStore';

const getCurrentTime = () => {
  return new Date().toISOString();
};

/**
 * A freshly-dropped node renders (and its InlineConfigPanel mounts) before the backend POST that
 * persists it completes, so a REST fetch of its config would 404. The "still initializing" state lives
 * on the component's own keyed store (the `initializing` flag, next to `loaded`) — a single source of
 * truth — so panels can skip that doomed fetch and show a loading state until the store is populated.
 *
 * Both helpers resolve the keyed store from `type`; they are no-ops for component types without a keyed
 * store (those panels don't take the keyed-store fetch path).
 */
export const markComponentInitializing = (type, componentId) => {
  if (!componentId) return;
  storeMap[type]?.getStore?.(componentId)?.setState({ initializing: true });
};

export const clearComponentInitializing = (type, componentId) => {
  if (!componentId) return;
  storeMap[type]?.getStore?.(componentId)?.setState({ initializing: false });
};

const storeTypeMap = {
  ai_agent: useAIAgentStore,
  ai_assistant: useAIAssistantTemplateStore,
  trigger_another_workflow: useTriggerAnotherWorkflowStore,
  trigger_from_external_app: useTriggerFromExternalStore,
  perform_action_in_external_app: usePerformActionExternalStore,
  trigger_via_email: useEmailTriggerStore,
  webhook: useWebhookImportStore,
  webhook_trigger: useWebhookTriggerStore,
  webhook_trigger_v2: useWebhookTriggerStore,
  trigger_from_a_workflow: useTriggerFromAWorkflowStore,
  trigger_on_database_update: useTriggerOnDatabaseUpdateStore,
  trigger_from_api_polling: useTriggerFromApiPollingStore,
  branch_paths: useBranchPathsStore,
  merge_workflow: useMergeWorkflowStore,
  time_delay: useTimeDelayStore,
  query_database_record: useQueryDatabaseStore,
  create_database_record: useCreateDatabaseRecordStore,
  update_database_record: useUpdateDatabaseRecordStore,
  api_export: useApiExportStore,
  code: useCodeStore,
  conditional_workflow: useConditionalWorkflowStore,
  document_editor: useDocumentStore,
  create_file: useCreateFileStore,
  schedule_start: useScheduleStartStore,
  session_variables: useSessionVariableStore,
  form: null,
  sequential_form: null,
  form_section: null,
  bulk_assessment: useBulkAssessmentStore,
  api_request_and_response: useAPIResReqStore,
  calculator: useCalculatorStore,
  email: useEmailStore,
  list_transform: useTransformStore,
  loop_through_list: useLoopThroughListStore,
  parse_json: useParseJSONStore,
  slack: useSlackStore,
  conditional_logic: useConditionalLogicStore,
  conditional_workflow_path: useConditionalWorkflowPathStore,
  await_external_event: useAwaitExternalEventStore,
  converge_threads: useConvergeThreadsStore,
  http_request: useHttpRequestStore,
  dynamic_form: useDynamicFormStore,
  dynamic_review_task: useDynamicReviewTaskStore,
  form_step: useFormStepStore,
  artifact: useArtifactStore,
  gateway: useGatewayStore,
  query_table_v2: useQueryTableV2Store,
  create_row_v2: useCreateRowV2Store,
  update_row_v2: useUpdateRowV2Store,
  trigger_table_v2: useTriggerTableV2Store,
  trigger_on_artifact: useTriggerOnArtifactStore,
  create_board_ticket: useCreateBoardTicketStore,
  get_board_ticket: useGetBoardTicketStore,
  update_board_ticket: useUpdateBoardTicketStore,
  trigger_on_board_ticket: useTriggerOnBoardTicketStore,
  custom_function: useCustomFunctionInstanceStore,
};

const formContainerMap = {
  form: FormBuilderContainer,
  sequential_form: SequentialFormContainer,
  form_section: SequentialFormContainer,
};

/**
 * Creates a fresh template snapshot for a given component type.
 * Resets the Zustand store to empty state, then reads back the template.
 */
const determineStoreType = type => {
  if (formContainerMap[type]) {
    const Container = formContainerMap[type];
    const instance = new Container();
    instance.type = type;
    return instance;
  }

  const useStore = storeTypeMap[type];
  if (!useStore) return null;

  const state = useStore.getState();
  state.initialiseEmpty();

  const freshState = useStore.getState();
  const endpoint = freshState.endpoint || type;
  const template = freshState.getTemplate();

  return {
    endpoint,
    type,
    template,
    name: freshState.name || '',
  };
};

/**
 * Creates component data structure based on component type
 */
const createComponentData = (store, type, lastModified) => {
  if (type === 'api_request_and_response') {
    return {
      schemaVersion: 2,
      name: store.name,
      requestData: store.template.requestData,
      sampleData: store.template.sampleData,
      responseData: store.template.responseData,
      lastModified,
      data: {
        curlTemplateData: store.template.data,
      },
    };
  }

  const templateData = store.template.data;
  return {
    schemaVersion: 2,
    ...(typeof templateData === 'object' && templateData !== null ? templateData : {}),
    name: store.name,
    lastModified,
  };
};

/**
 * Initializes a new component in the workflow
 */
export const initializeComponent = async (type, componentId) => {
  const store = determineStoreType(type);
  if (!store) {
    throw new Error(`Invalid component type: ${type}`);
  }

  const lastModified = getCurrentTime();
  const componentData = createComponentData(store, type, lastModified);

  const data = {
    componentData,
    componentId,
    type,
    description: '',
    status: null,
  };

  const endpoint = store.endpoint || type;

  try {
    const response = await send_component_save_request(
      `component-service/${endpoint}/data`,
      data,
      '',
      'POST',
      false
    );

    // Populate the keyed store so auto-save works immediately without
    // waiting for a page refresh or REST fallback.
    const config = storeMap[type];
    if (config?.getStore && config.setter) {
      const keyedStore = config.getStore(componentId);
      const state = keyedStore.getState();
      if (!state.loaded && typeof state[config.setter] === 'function') {
        state[config.setter]({
          componentId,
          type,
          description: '',
          componentData,
        });
        keyedStore.setState({
          loaded: true,
          name: componentData.name || '',
          componentDescription: '',
        });
        protectLoadedStore(keyedStore);
      }
    }

    return response;
  } catch (error) {
    console.error('Error initializing component:', error);
    throw error;
  } finally {
    clearComponentInitializing(type, componentId);
  }
};
